import React, { useState } from 'react';
import { X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateBatchModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    batchName: '',
    batchDescription: '',
    batchPriority: 'medium',
    expirationWindowStart: '',
    expirationWindowEnd: '',
    documentTypes: ['work_permit', 'visa', 'driver_license', 'passport'],
    autoProcessEnabled: true,
    autoNotifyEmployees: true
  });

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    try {
      await onSubmit({
        batchName: formData?.batchName,
        batchDescription: formData?.batchDescription,
        batchPriority: formData?.batchPriority,
        expirationWindowStart: formData?.expirationWindowStart,
        expirationWindowEnd: formData?.expirationWindowEnd,
        documentTypes: formData?.documentTypes,
        autoProcessEnabled: formData?.autoProcessEnabled,
        autoNotifyEmployees: formData?.autoNotifyEmployees,
        createdBy: 'current_user@example.com'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Renewal Batch</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch Name *
            </label>
            <Input
              type="text"
              value={formData?.batchName}
              onChange={(e) => setFormData({ ...formData, batchName: e?.target?.value })}
              placeholder="e.g., Q1 2026 Renewal Batch"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData?.batchDescription}
              onChange={(e) => setFormData({ ...formData, batchDescription: e?.target?.value })}
              placeholder="Describe the purpose of this batch"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <Select
                value={formData?.batchPriority}
                onChange={(e) => setFormData({ ...formData, batchPriority: e?.target?.value })}
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Window Start *
              </label>
              <Input
                type="date"
                value={formData?.expirationWindowStart}
                onChange={(e) => setFormData({ ...formData, expirationWindowStart: e?.target?.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Window End *
              </label>
              <Input
                type="date"
                value={formData?.expirationWindowEnd}
                onChange={(e) => setFormData({ ...formData, expirationWindowEnd: e?.target?.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Automation Settings
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData?.autoProcessEnabled}
                  onChange={(e) => setFormData({ ...formData, autoProcessEnabled: e?.target?.checked })}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Enable auto-processing</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData?.autoNotifyEmployees}
                  onChange={(e) => setFormData({ ...formData, autoNotifyEmployees: e?.target?.checked })}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Auto-notify employees</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBatchModal;